import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import Loader from 'react-loader-spinner';
import { FiClock, FiDownload, FiSearch, FiTrash2, FiX } from 'react-icons/fi';
import { API_URLS, getTeamImageUrl } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { ROLE, useAdminAccess } from '../utilities/adminAccess';
import { extractEntity, extractList, champFields } from '../utilities/tournamentsApi';
import { extractMatchesPage, matchRequestBody, MATCHES_PAGE_SIZE, scoreText } from '../utilities/matchesApi';
import { resolveMatchTimezone, utcKickoffToWall } from '../utilities/matchKickoff';
import {
  autoFillSlotsFromPreferences,
  attachScheduledMatchesToSlots,
  downloadTextFile,
  extractScheme,
  extractSchemes,
  formatClock,
  formatDayMonthTime,
  formatSlotsTxt,
  generateTimeSlots,
  groupSlotsForDisplay,
  mergeSlots,
  preferencesFromMatches,
  resolveSchedulerTimezone,
  resolveWeekDate,
  SCHEDULER_TIMEZONE_OPTIONS,
  slotCalendarDate,
  slotIdentity,
  slotMatchWishStatus,
  slotPayload,
  summarizeMatchPreferences,
  weekRangeFromDate,
  wishSlotPayload,
} from '../utilities/schedulerApi';
import StadiumCrumbs from '../components/StadiumCrumbs';
import CustomModal from '../components/CustomModal';
import defaultTeamLogo from '../images/defaultTeamLogo.png';
import '../styles/stadiumsManagement.css';
import '../styles/createMatchModal.css';
import '../styles/timeSlots.css';

const MATCH_LIMIT = MATCHES_PAGE_SIZE;

function FitsMark({ value, title }) {
  if (value === 1) return <span className="ts-fit is-yes" title={title}>+</span>;
  if (value === 0) return <span className="ts-fit is-no" title={title}>−</span>;
  return <span className="ts-fit is-na" title={title}>?</span>;
}

function TeamLogo({ team, image }) {
  const src = getTeamImageUrl(team?.image || image) || defaultTeamLogo;
  return (
    <img
      className="ts-logo"
      src={src}
      alt=""
      onError={(event) => { event.currentTarget.src = defaultTeamLogo; }}
    />
  );
}

function isMatchFinished(match) {
  const status = Number(match?.status);
  return status === 2 || status === 5;
}

function MatchTeams({ match }) {
  const home = match?.home_team || {};
  const guest = match?.guest_team || {};
  const homeName = home.name || match?.home_team_name || '';
  const guestName = guest.name || match?.guest_team_name || '';
  const finished = isMatchFinished(match);
  return (
    <span className="ts-match-teams">
      <TeamLogo team={home} image={match?.home_team_image} />
      <span className="ts-match-name">{homeName || '—'}</span>
      {finished ? <span className="ts-match-score">{scoreText(match)}</span> : <span className="ts-match-dash">–</span>}
      <TeamLogo team={guest} image={match?.guest_team_image} />
      <span className="ts-match-name">{guestName || '—'}</span>
    </span>
  );
}

function StadiumPicker({ stadium, onSelect, onClear, disabled }) {
  const dispatch = useDispatch();
  const { t } = useI18n();
  const { champId } = useChampionships();
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const timerRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const onDocClick = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (!open || disabled) return undefined;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => loadStadiums(query), query ? 250 : 0);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [query, open, disabled, champId]);

  const loadStadiums = async (search) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const body = champFields(champId);
      if (search && search.trim()) body.search = search.trim();
      const response = await authorizedPost(dispatch, API_URLS.getAllAdminStadiums, body);
      if (requestId !== requestIdRef.current) return;
      const list = Array.isArray(response.data) ? response.data : response.data?.data || [];
      setItems(list);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      setItems([]);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  if (stadium && !open) {
    return (
      <div className="ts-picked">
        <button
          type="button"
          className="ts-picked-main"
          disabled={disabled}
          onClick={() => {
            setQuery('');
            setOpen(true);
          }}
        >
          <strong>{stadium.name}</strong>
        </button>
        <button type="button" className="ts-icon-clear" onClick={onClear} disabled={disabled} aria-label={t('tsClearStadium')}>
          ×
        </button>
      </div>
    );
  }

  return (
    <div className="ts-picker" ref={wrapRef}>
      <div className="st-search">
        <FiSearch className="st-search-icon" />
        <input
          type="text"
          className="st-search-input"
          placeholder={t('tsStadiumPh')}
          value={query}
          disabled={disabled}
          autoFocus={Boolean(stadium)}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
        />
        {stadium ? (
          <button type="button" className="st-search-clear" onClick={onClear} disabled={disabled} aria-label={t('tsClearStadium')}>
            ×
          </button>
        ) : null}
      </div>
      {open ? (
        <div className="ts-picker-menu">
          {loading ? <div className="ts-picker-empty">{t('tsStadiumLoading')}</div> : null}
          {!loading && !items.length ? <div className="ts-picker-empty">{t('tsStadiumEmpty')}</div> : null}
          {items.map((item) => (
            <button
              type="button"
              key={item.id}
              className="ts-picker-item"
              onClick={() => {
                onSelect(item);
                setQuery('');
                setOpen(false);
              }}
            >
              <strong>{item.name}</strong>
              {item.address ? <span>{item.address}</span> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function TimeSlots() {
  const dispatch = useDispatch();
  const { t, lang } = useI18n();
  const { champId, selected: championship, loading: champsLoading } = useChampionships();
  const matchesAccess = useAdminAccess('matches');
  const stadiumsAccess = useAdminAccess('stadiums');
  const canWriteMatches = matchesAccess.canWrite || matchesAccess.roles.includes(ROLE.referee);
  const canWriteSchemes = stadiumsAccess.canWrite;

  const [countries, setCountries] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [stages, setStages] = useState([]);
  const [countryId, setCountryId] = useState('');
  const [leagueId, setLeagueId] = useState('');
  const [seasonId, setSeasonId] = useState('');
  const [stageId, setStageId] = useState('');
  const [matchSearch, setMatchSearch] = useState('');
  const [foundMatches, setFoundMatches] = useState([]);
  const [matchesTotal, setMatchesTotal] = useState(0);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [loadingMoreMatches, setLoadingMoreMatches] = useState(false);
  const [selectedMatches, setSelectedMatches] = useState([]);
  const [activeMatchId, setActiveMatchId] = useState(null);

  const [stadium, setStadium] = useState(null);
  const [pitches, setPitches] = useState([]);
  const [pitchId, setPitchId] = useState('');
  const [timezoneOverride, setTimezoneOverride] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [duration, setDuration] = useState(30);

  const [schemes, setSchemes] = useState([]);
  const [schemeId, setSchemeId] = useState('');
  const [schemeName, setSchemeName] = useState('');
  const [loadedSchemeId, setLoadedSchemeId] = useState(null);
  const [slots, setSlots] = useState([]);
  const [detachedMatchIds, setDetachedMatchIds] = useState([]);
  const [loadingSchemes, setLoadingSchemes] = useState(false);

  const [busy, setBusy] = useState('');
  const [infoModal, setInfoModal] = useState({ text: '', type: '' });
  const [confirmModal, setConfirmModal] = useState(null);
  const matchesRef = useRef([]);
  matchesRef.current = foundMatches;
  const requestIdRef = useRef(0);
  const searchTimerRef = useRef(null);

  const selectedIds = useMemo(() => new Set(selectedMatches.map((item) => item.id)), [selectedMatches]);
  const weekDate = useMemo(() => resolveWeekDate(selectedMatches, slots), [selectedMatches, slots]);
  const groupedSlots = useMemo(() => groupSlotsForDisplay(slots, weekDate, lang), [slots, weekDate, lang]);
  const selectedPitch = pitches.find((item) => Number(item.id) === Number(pitchId)) || null;
  const needPitch = pitches.length > 0;
  const startValue = startDate && startTime ? `${startDate}T${startTime}` : '';
  const endValue = (endDate || startDate) && endTime ? `${endDate || startDate}T${endTime}` : '';
  const canGenerate = Boolean(stadium) && (!needPitch || selectedPitch) && startValue && endValue && Number(duration) > 0;
  const canOpenWishes = selectedMatches.length > 0 && slots.length > 0 && canWriteMatches;
  const canAssign = canWriteMatches && (
    slots.some((slot) => (slot.pendingMatch || slot.match)?.id)
    || detachedMatchIds.length > 0
  );
  const preferences = useMemo(() => preferencesFromMatches(selectedMatches), [selectedMatches]);
  const selectedPreferences = preferences;
  const canAutoAssign = slots.length > 0 && selectedMatches.length > 0 && canWriteMatches;

  const matchesInSlots = useMemo(() => {
    const ids = new Set();
    slots.forEach((slot) => {
      const match = slot.pendingMatch || slot.match;
      if (match?.id) ids.add(Number(match.id));
    });
    return ids;
  }, [slots]);

  const selectedLeague = useMemo(
    () => leagues.find((item) => Number(item.id) === Number(leagueId)) || null,
    [leagues, leagueId],
  );
  const resolvedTimezone = useMemo(
    () => resolveSchedulerTimezone(
      stadium,
      championship,
      selectedLeague,
      timezoneOverride,
      ...selectedMatches,
    ),
    [stadium, championship, selectedLeague, timezoneOverride, selectedMatches],
  );

  const showInfo = (text, type = 'success') => setInfoModal({ text, type });
  const closeInfo = () => setInfoModal({ text: '', type: '' });
  const closeConfirm = () => setConfirmModal(null);

  useEffect(() => {
    setCountries([]);
    setLeagues([]);
    setSeasons([]);
    setStages([]);
    setCountryId('');
    setLeagueId('');
    setSeasonId('');
    setStageId('');
    setMatchSearch('');
    setFoundMatches([]);
    setSelectedMatches([]);
    setActiveMatchId(null);
    setStadium(null);
    setPitches([]);
    setPitchId('');
    setTimezoneOverride('');
    setSchemes([]);
    setSchemeId('');
    setSchemeName('');
    setLoadedSchemeId(null);
    setSlots([]);
    setDetachedMatchIds([]);
    setInfoModal({ text: '', type: '' });
    setConfirmModal(null);
    if (!champId) return undefined;
    loadCountries();
    loadSchemes();
    return undefined;
  }, [champId]);

  useEffect(() => {
    if (!seasonId) {
      setFoundMatches([]);
      setMatchesTotal(0);
      return undefined;
    }
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    const delay = matchSearch.trim() ? 350 : 0;
    searchTimerRef.current = setTimeout(() => {
      loadMatches(0, false);
    }, delay);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [champId, countryId, leagueId, seasonId, stageId, matchSearch]);

  const flash = (text, type = 'success') => {
    if (!text) return;
    // Успех показываем только для назначения времени; остальное — без модалки.
    if (type !== 'error') return;
    showInfo(text, type);
  };

  const applyAssignedMatchTimes = (updated = []) => {
    const byId = new Map(
      (updated || [])
        .map((item) => [Number(item.match_id ?? item.id), item])
        .filter(([id]) => id),
    );
    if (!byId.size) return (match) => match;
    const patchMatch = (match) => {
      if (!match?.id) return match;
      const next = byId.get(Number(match.id));
      if (!next) return match;
      const tz = resolveMatchTimezone(match, next, resolvedTimezone);
      const rawDate = String(next.date || '').slice(0, 10);
      const rawTime = next.time != null ? String(next.time).slice(0, 5) : '';
      const wall = rawDate && rawTime
        ? utcKickoffToWall(rawDate, rawTime, tz)
        : null;
      return {
        ...match,
        date: wall?.date || rawDate || match.date || '',
        time: wall?.time || rawTime || match.time || '',
        date_utc: rawDate || match.date_utc || '',
        time_utc: rawTime || match.time_utc || '',
        no_date: false,
        timezone: tz,
        stadium_id: next.stadium_id != null ? Number(next.stadium_id) : match.stadium_id,
        pitch_id: next.pitch_id != null ? Number(next.pitch_id) : match.pitch_id,
      };
    };
    setSelectedMatches((prev) => prev.map(patchMatch));
    setFoundMatches((prev) => prev.map(patchMatch));
    return patchMatch;
  };

  const applyClearedMatchTimes = (clearedIds = []) => {
    const ids = new Set((clearedIds || []).map(Number).filter(Boolean));
    if (!ids.size) return;
    const patchMatch = (match) => {
      if (!match?.id || !ids.has(Number(match.id))) return match;
      return {
        ...match,
        date: '',
        time: '',
        no_date: true,
        stadium_id: null,
        pitch_id: null,
        stadium: null,
        pitch: null,
      };
    };
    setSelectedMatches((prev) => prev.map(patchMatch));
    setFoundMatches((prev) => prev.map(patchMatch));
  };

  const markMatchDetached = (matchId) => {
    const id = Number(matchId);
    if (!id) return;
    setDetachedMatchIds((prev) => (prev.includes(id) ? prev : prev.concat(id)));
  };

  const unmarkMatchDetached = (matchId) => {
    const id = Number(matchId);
    if (!id) return;
    setDetachedMatchIds((prev) => prev.filter((item) => item !== id));
  };

  const loadCountries = async () => {
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminCountries, champFields(champId));
      setCountries(extractList(response.data).map((item) => ({
        id: Number(item.id),
        name: item.name || '',
      })).filter((item) => item.id > 0));
    } catch (error) {
      setCountries([]);
      flash(getApiErrorMessage(error, t('tsLoadCountriesError')), 'error');
    }
  };

  const loadSchemes = async () => {
    setLoadingSchemes(true);
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminSchemes, {
        ...champFields(champId),
      });
      setSchemes(extractSchemes(response.data));
    } catch (error) {
      setSchemes([]);
      flash(getApiErrorMessage(error, t('tsLoadSchemesError')), 'error');
    } finally {
      setLoadingSchemes(false);
    }
  };

  const handleCountryChange = async (value) => {
    setCountryId(value);
    setLeagueId('');
    setSeasonId('');
    setStageId('');
    setLeagues([]);
    setSeasons([]);
    setStages([]);
    setFoundMatches([]);
    if (!value) return;
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminCountry, { id: Number(value) });
      const nextLeagues = extractList(extractEntity(response.data).leagues).map((item) => ({
        ...item,
        id: Number(item.id || item.league_id),
        name: item.name || item.league_name || '',
        seasons: extractList(item.seasons),
      })).filter((item) => item.id > 0);
      setLeagues(nextLeagues);
    } catch (error) {
      setLeagues([]);
      flash(getApiErrorMessage(error, t('tsLoadLeaguesError')), 'error');
    }
  };

  const handleLeagueChange = async (value) => {
    setLeagueId(value);
    setSeasonId('');
    setStageId('');
    setSeasons([]);
    setStages([]);
    setFoundMatches([]);
    if (!value) return;
    const league = leagues.find((item) => Number(item.id) === Number(value));
    let nextSeasons = extractList(league?.seasons).map((item) => ({
      id: Number(item.season_id || item.id),
      name: item.season_name || item.name || '',
    })).filter((item) => item.id > 0);
    if (!nextSeasons.length) {
      try {
        const response = await authorizedPost(dispatch, API_URLS.getAdminLeague, { id: Number(value) });
        nextSeasons = extractList(extractEntity(response.data).seasons).map((item) => ({
          id: Number(item.season_id || item.id),
          name: item.season_name || item.name || '',
        })).filter((item) => item.id > 0);
      } catch (error) {
        nextSeasons = [];
        flash(getApiErrorMessage(error, t('tsLoadSeasonsError')), 'error');
      }
    }
    setSeasons(nextSeasons);
  };

  const handleSeasonChange = async (value) => {
    setSeasonId(value);
    setStageId('');
    setStages([]);
    if (!value || !leagueId) return;
    try {
      const response = await authorizedPost(dispatch, `/leagues/${leagueId}/getLeagueStages`, {
        league_id: Number(leagueId),
        season_id: Number(value),
      });
      setStages(extractList(response.data).map((item) => ({
        id: Number(item.id || item.stage_id),
        name: item.name || item.stage_name || item.short_name || '',
      })).filter((item) => item.id > 0));
    } catch (error) {
      setStages([]);
      flash(getApiErrorMessage(error, t('tsLoadStagesError')), 'error');
    }
  };

  const loadMatches = async (offset, append) => {
    if (!champId || !seasonId) return;
    const requestId = ++requestIdRef.current;
    if (append) setLoadingMoreMatches(true);
    else setLoadingMatches(true);
    try {
      const body = matchRequestBody(champId, {
        limit: MATCH_LIMIT,
        offset,
        country_id: Number(countryId) || undefined,
        league_id: Number(leagueId) || undefined,
        season_id: Number(seasonId) || undefined,
        stage_id: Number(stageId) || undefined,
        unplayed: true,
      });
      const query = matchSearch.trim();
      if (query) body.search = query;
      const response = await authorizedPost(dispatch, API_URLS.getAdminMatches, body);
      if (requestId !== requestIdRef.current) return;
      const page = extractMatchesPage(response.data);
      const next = append ? matchesRef.current.concat(page.matches) : page.matches;
      setFoundMatches(next);
      setMatchesTotal(page.total != null ? page.total : next.length);
      // Подтянуть wishes[] у уже выбранных матчей с текущей страницы.
      setSelectedMatches((prev) => {
        if (!prev.length) return prev;
        const byId = new Map(page.matches.map((item) => [Number(item.id), item]));
        let changed = false;
        const mapped = prev.map((item) => {
          const fresh = byId.get(Number(item.id));
          if (!fresh || !Array.isArray(fresh.wishes)) return item;
          changed = true;
          return { ...item, wishes: fresh.wishes };
        });
        return changed ? mapped : prev;
      });
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      if (!append) {
        setFoundMatches([]);
        setMatchesTotal(0);
      }
      flash(getApiErrorMessage(error, t('tsLoadMatchesError')), 'error');
    } finally {
      if (requestId === requestIdRef.current) {
        setLoadingMatches(false);
        setLoadingMoreMatches(false);
      }
    }
  };

  const addMatch = (match) => {
    if (!match?.id || selectedIds.has(match.id) || isMatchFinished(match)) return;
    setSelectedMatches((prev) => prev.concat(match));
  };

  const removeMatch = (matchId) => {
    const id = Number(matchId);
    setSlots((prev) => {
      const wasAssigned = prev.some((slot) => Number(slot.match?.id) === id);
      if (wasAssigned) markMatchDetached(id);
      return prev.map((slot) => {
        const placed = slot.pendingMatch || slot.match;
        if (Number(placed?.id) === id) {
          return { ...slot, pendingMatch: null, match: null };
        }
        return slot;
      });
    });
    setSelectedMatches((prev) => prev.filter((item) => item.id !== matchId));
    if (Number(activeMatchId) === id) setActiveMatchId(null);
  };

  const handleStadiumSelect = async (nextStadium) => {
    setStadium(nextStadium);
    setPitchId('');
    setPitches([]);
    if (!nextStadium?.id) return;
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminStadium, {
        id: Number(nextStadium.id),
        ...champFields(champId),
      });
      const entity = extractEntity(response.data) || {};
      setPitches(extractList(entity.pitches));
    } catch (error) {
      setPitches([]);
      flash(getApiErrorMessage(error, t('tsLoadPitchesError')), 'error');
    }
  };

  const handleAddSlots = () => {
    const result = generateTimeSlots({
      startValue,
      endValue,
      durationMinutes: duration,
      stadium,
      pitch: selectedPitch,
      lang,
    });
    if (result.error) {
      flash(t(result.error), 'error');
      if (!result.slots.length) return;
    }
    setSlots((prev) => mergeSlots(prev, result.slots));
    if (result.slots.length) flash(t('tsSlotsAdded', { count: result.slots.length }));
  };

  const removeSlot = (clientKey) => {
    setSlots((prev) => prev.filter((slot) => slot.clientKey !== clientKey));
  };

  const placeMatchOnSlot = (clientKey) => {
    const match = selectedMatches.find((item) => Number(item.id) === Number(activeMatchId));
    if (!match) return;
    const target = slots.find((slot) => slot.clientKey === clientKey);
    // Если на слоте ещё висит чужой серверный match — снимем его в clear_match_ids.
    const previousId = Number(target?.match?.id);
    if (previousId && previousId !== Number(match.id)) markMatchDetached(previousId);
    unmarkMatchDetached(match.id);
    setSlots((prev) => prev.map((slot) => {
      if (slot.clientKey === clientKey) return { ...slot, pendingMatch: match, match: null };
      if (Number(slot.pendingMatch?.id) === Number(match.id)) return { ...slot, pendingMatch: null };
      // Тот же матч переносим со старого слота — не в clear, он уходит в items.
      if (Number(slot.match?.id) === Number(match.id)) return { ...slot, match: null };
      return slot;
    }));
  };

  const clearSlotMatch = (clientKey) => {
    const target = slots.find((slot) => slot.clientKey === clientKey);
    // Сняли матч, который уже был назначен на сервере (slot.match) — запомним для clear.
    if (target?.match?.id) markMatchDetached(target.match.id);
    setSlots((prev) => prev.map((slot) => (
      slot.clientKey === clientKey ? { ...slot, pendingMatch: null, match: null } : slot
    )));
  };

  const handleLoadScheme = async () => {
    const scheme = schemes.find((item) => Number(item.id) === Number(schemeId));
    if (!scheme) return;
    if (!weekDate) {
      flash(t('tsNeedWeekDate'), 'error');
      return;
    }
    setBusy('load');
    setLoadedSchemeId(scheme.id);
    setSchemeName(scheme.name || '');
    try {
      const body = {
        ...champFields(champId),
        date: weekDate,
        timezone: resolvedTimezone,
      };
      const selectedIdsList = selectedMatches.map((item) => Number(item.id)).filter(Boolean);
      // Не слать пустой match_ids — на сервере [] может обнулить выборку (scheduled_raw: 0).
      if (selectedIdsList.length) body.match_ids = selectedIdsList;

      const response = await authorizedPost(dispatch, API_URLS.getAdminSchemes, body);
      const list = extractSchemes(response.data);
      const fresh = list.find((item) => Number(item.id) === Number(scheme.id)) || scheme;
      let nextSlots = (fresh.slots || scheme.slots || []).map((slot) => ({
        ...slot,
        pendingMatch: null,
      }));

      // Если сервер не прикрепил матчи — дотягиваем матчи недели и склеиваем на клиенте.
      if (nextSlots.some((slot) => !slot.match?.id)) {
        const { from, to } = weekRangeFromDate(weekDate);
        let weekMatches = selectedMatches.filter((item) => {
          const date = String(item.date || '').slice(0, 10);
          return date && from && to && date >= from && date <= to;
        });
        try {
          const matchesResponse = await authorizedPost(dispatch, API_URLS.getAdminMatches, matchRequestBody(champId, {
            date_from: from || undefined,
            date_to: to || undefined,
            country_id: Number(countryId) || undefined,
            league_id: Number(leagueId) || undefined,
            season_id: Number(seasonId) || undefined,
          }));
          const page = extractMatchesPage(matchesResponse.data);
          const byId = new Map(weekMatches.map((item) => [Number(item.id), item]));
          (page.matches || []).forEach((item) => {
            if (item?.id) byId.set(Number(item.id), item);
          });
          weekMatches = [...byId.values()];
        } catch (_error) {
          // оставляем то, что уже есть в selectedMatches
        }
        nextSlots = attachScheduledMatchesToSlots(
          nextSlots,
          weekMatches,
          weekDate,
          resolvedTimezone,
        );
      }

      setSlots(nextSlots);
      setDetachedMatchIds([]);
      setLoadedSchemeId(fresh.id);
      setSchemeName(fresh.name || scheme.name || '');
      const assigned = nextSlots.map((slot) => slot.match).filter((match) => match?.id);
      if (assigned.length) {
        setSelectedMatches((prev) => {
          const ids = new Set(prev.map((item) => Number(item.id)));
          const extras = assigned.filter((match) => !ids.has(Number(match.id)));
          return extras.length ? prev.concat(extras) : prev;
        });
      }
    } catch (error) {
      setSlots((scheme.slots || []).map((slot) => ({ ...slot, pendingMatch: null })));
      flash(getApiErrorMessage(error, t('tsLoadSchemesError')), 'error');
    } finally {
      setBusy('');
    }
  };

  const handleDeleteScheme = () => {
    const id = Number(schemeId || loadedSchemeId);
    if (!id || !canWriteSchemes) return;
    setConfirmModal({
      title: t('tsDeleteScheme'),
      text: t('tsDeleteSchemeConfirm'),
      action: 'scheme',
    });
  };

  const executeDeleteScheme = async () => {
    const id = Number(schemeId || loadedSchemeId);
    if (!id || !canWriteSchemes) return;
    setConfirmModal(null);
    setBusy('deleteScheme');
    try {
      await authorizedPost(dispatch, API_URLS.deleteAdminScheme, { id });
      if (Number(loadedSchemeId) === id) {
        setLoadedSchemeId(null);
        setSlots([]);
        setSchemeName('');
      }
      setSchemeId('');
      await loadSchemes();
      flash(t('tsSchemeDeleted'));
    } catch (error) {
      flash(getApiErrorMessage(error, t('tsDeleteSchemeError')), 'error');
    } finally {
      setBusy('');
    }
  };

  const handleOpenWishes = async () => {
    if (!canOpenWishes) return;
    if (!weekDate) {
      flash(t('tsNeedWeekDate'), 'error');
      return;
    }
    setBusy('open');
    try {
      const response = await authorizedPost(dispatch, API_URLS.openAdminWishes, {
        date: weekDate,
        timezone: resolvedTimezone,
        match_ids: selectedMatches.map((item) => item.id),
        slots: slots.map(wishSlotPayload),
      });
      const skippedItems = Array.isArray(response.data?.skipped) ? response.data.skipped : [];
      const skipped = skippedItems.length;
      // Локально открыть слоты как пожелания (?/?), без перезагрузки списка игр.
      setSelectedMatches((prev) => prev.map((match) => ({
        ...match,
        wishes: slots.map((slot) => ({
          match_id: match.id,
          id: match.id,
          day: slot.day,
          hour: slot.hour,
          minute: slot.minute,
          time: slot.time || formatClock(slot.hour, slot.minute),
          date: slotCalendarDate(slot, weekDate) || '',
          stadium_id: slot.stadium_id,
          pitch_id: slot.pitch_id,
          home_fits: null,
          away_fits: null,
          assigned: false,
        })),
      })));
      const reasons = skippedItems.map((item) => {
        const id = item.match_id || item.id || '';
        const reason = item.reason || '';
        return id ? `#${id}: ${reason}` : reason;
      }).filter(Boolean).join('\n');
      const text = skipped
        ? `${t('tsWishesOpened', { skipped })}\n${t('tsWishesSkippedHint')}${reasons ? `\n${reasons}` : ''}`
        : '';
      if (text) flash(text, 'error');
    } catch (error) {
      flash(getApiErrorMessage(error, t('tsOpenWishesError')), 'error');
    } finally {
      setBusy('');
    }
  };

  const handleDeleteWishes = () => {
    if (!selectedMatches.length || !canWriteMatches) return;
    setConfirmModal({
      title: t('tsDeleteWishes'),
      text: t('tsDeleteWishesConfirm'),
      action: 'wishes',
    });
  };

  const executeDeleteWishes = async () => {
    if (!selectedMatches.length || !canWriteMatches) return;
    setConfirmModal(null);
    setBusy('deleteWishes');
    try {
      const response = await authorizedPost(dispatch, API_URLS.deleteAdminWishes, {
        match_ids: selectedMatches.map((item) => item.id),
      });
      const deleted = response.data?.deleted ?? 0;
      setSlots((prev) => prev.map((slot) => ({ ...slot, match: null, pendingMatch: null })));
      setSelectedMatches((prev) => prev.map((item) => ({ ...item, wishes: [] })));
      flash(t('tsWishesDeleted', { deleted }));
    } catch (error) {
      flash(getApiErrorMessage(error, t('tsDeleteWishesError')), 'error');
    } finally {
      setBusy('');
    }
  };

  const submitMatchSlots = async (workingSlots) => {
    const seen = new Set();
    const items = [];
    workingSlots.forEach((slot) => {
      const match = slot.pendingMatch || slot.match;
      const matchId = Number(match?.id);
      if (!matchId || seen.has(matchId)) return;
      seen.add(matchId);
      items.push(
        slot.id
          ? { match_id: matchId, slot_id: slot.id }
          : { match_id: matchId, slot: slotPayload(slot) }
      );
    });
    const stillOnSlot = new Set(items.map((item) => Number(item.match_id)));
    const clearMatchIds = detachedMatchIds
      .map(Number)
      .filter((id) => id && !stillOnSlot.has(id));
    if (!items.length && !clearMatchIds.length) return { updated: [], failed: [], cleared: [] };
    if (!weekDate) {
      flash(t('tsNeedWeekDate'), 'error');
      return null;
    }
    const body = {
      date: weekDate,
      timezone: resolvedTimezone,
      items,
    };
    if (clearMatchIds.length) body.clear_match_ids = clearMatchIds;
    const response = await authorizedPost(dispatch, API_URLS.setAdminMatchSlots, body);
    const updated = Array.isArray(response.data?.updated) ? response.data.updated : [];
    const failed = Array.isArray(response.data?.failed) ? response.data.failed : [];
    const clearedRaw = Array.isArray(response.data?.cleared) ? response.data.cleared : clearMatchIds;
    const clearedIds = clearedRaw.map((item) => Number(item?.match_id ?? item?.id ?? item)).filter(Boolean);
    const updatedIds = new Set(updated.map((item) => Number(item.match_id ?? item.id)).filter(Boolean));
    const patchMatch = applyAssignedMatchTimes(updated);
    applyClearedMatchTimes(clearedIds);
    const locallyUpdated = workingSlots.map((slot) => {
      const match = slot.pendingMatch || slot.match;
      if (match && updatedIds.has(Number(match.id))) {
        return { ...slot, match: patchMatch(match), pendingMatch: null };
      }
      return slot;
    });
    setSlots(locallyUpdated);
    setDetachedMatchIds((prev) => prev.filter((id) => !clearedIds.includes(Number(id))));
    return { updated, failed, cleared: clearedIds };
  };

  const handleAssignTime = async () => {
    if (!canAssign) return;
    setBusy('assign');
    try {
      const result = await submitMatchSlots(slots);
      if (!result) return;
      const { updated, failed, cleared } = result;
      const reasons = failed.map((item) => {
        const id = item.match_id || item.id || '';
        const reason = item.reason || '';
        return id ? `#${id}: ${reason}` : reason;
      }).filter(Boolean).join('\n');
      if (failed.length) {
        flash(`${t('tsAssignPartial', { ok: updated.length, fail: failed.length })}${reasons ? `\n${reasons}` : ''}`, 'error');
      } else if (updated.length || (cleared && cleared.length)) {
        const count = updated.length || cleared.length;
        showInfo(t('tsAssignOk', { count }), 'success');
      }
    } catch (error) {
      flash(getApiErrorMessage(error, t('tsAssignError')), 'error');
    } finally {
      setBusy('');
    }
  };

  const handleAutoAssign = () => {
    if (!canAutoAssign) return;
    if (!weekDate) {
      flash(t('tsNeedWeekDate'), 'error');
      return;
    }
    // Только локально (pendingMatch). На сервер — по кнопке «Назначить время».
    const { slots: filled, placed } = autoFillSlotsFromPreferences(
      slots,
      selectedPreferences,
      selectedMatches,
    );
    setSlots(filled);
    if (!placed) {
      flash(t('tsAutoAssignNone'), 'error');
      return;
    }
    flash(t('tsAutoAssignOk', { count: placed }));
  };

  const handleSaveScheme = async () => {
    if (!canWriteSchemes) return;
    const name = schemeName.trim();
    if (!name) {
      flash(t('tsNeedSchemeName'), 'error');
      return;
    }
    if (!champId) {
      flash(t('gmNeedChamp'), 'error');
      return;
    }
    setBusy('save');
    try {
      const payload = {
        name,
        ...champFields(champId),
        slots: slots.map(slotPayload),
      };
      const response = loadedSchemeId
        ? await authorizedPost(dispatch, API_URLS.changeAdminScheme, { id: loadedSchemeId, ...payload })
        : await authorizedPost(dispatch, API_URLS.addAdminScheme, payload);
      const saved = extractScheme(response.data);
      if (saved?.id) {
        const pendingByKey = new Map();
        slots.forEach((slot) => {
          if (slot.pendingMatch) pendingByKey.set(slotIdentity(slot), slot.pendingMatch);
        });
        const nextSlots = (saved.slots || []).map((slot) => ({
          ...slot,
          pendingMatch: pendingByKey.get(slotIdentity(slot)) || null,
        }));
        setLoadedSchemeId(saved.id);
        setSchemeId(String(saved.id));
        setSchemeName(saved.name || name);
        setSlots(nextSlots.length ? nextSlots : slots);
      }
      await loadSchemes();
      flash(t('tsSchemeSaved'));
    } catch (error) {
      flash(getApiErrorMessage(error, t('tsSaveSchemeError')), 'error');
    } finally {
      setBusy('');
    }
  };

  const handleSaveText = () => {
    const text = formatSlotsTxt(slots, weekDate, lang);
    if (!text) {
      flash(t('tsTxtEmpty'), 'error');
      return;
    }
    downloadTextFile(`${text}\n`, `slots-${weekDate || 'scheme'}.txt`);
    flash(t('tsTxtSaved'));
  };

  const renderWishLines = (label, yesTimes, noTimes) => (
    <div className="ts-wish-side">
      <strong>{label}</strong>
      {yesTimes ? (
        <div className="ts-wish-yes">{t('tsWishFits', { times: yesTimes })}</div>
      ) : null}
      {noTimes ? (
        <div className="ts-wish-no">{t('tsWishNoFits', { times: noTimes })}</div>
      ) : null}
      {!yesTimes && !noTimes ? (
        <div className="ts-wish-na">{t('tsWishNoAnswer')}</div>
      ) : null}
    </div>
  );

  const renderMatchRow = (match, mode) => {
    const selected = selectedIds.has(match.id);
    const active = Number(activeMatchId) === Number(match.id);
    const inSlot = mode === 'selected' && matchesInSlots.has(Number(match.id));
    const homeName = match.home_team?.name || match.home_team_name || t('tsWishHome');
    const guestName = match.guest_team?.name || match.guest_team_name || t('tsWishAway');
    const wishSummary = mode === 'selected'
      ? summarizeMatchPreferences(match.id, preferences, weekDate, lang)
      : null;
    return (
      <div key={`${mode}-${match.id}`} className="ts-match-wrap">
        <div
          className={`ts-match ${selected && mode === 'found' ? 'is-added' : ''} ${active && mode === 'selected' ? 'is-active' : ''} ${inSlot ? 'is-in-slot' : ''}`}
        >
          <button
            type="button"
            className="ts-match-main"
            onClick={() => {
              if (mode === 'found') addMatch(match);
              else setActiveMatchId(active ? null : match.id);
            }}
          >
            <MatchTeams match={match} />
            <span className="ts-match-meta">
              {[
                formatDayMonthTime(match.date, match.time),
                match.stage_name,
              ].filter(Boolean).join(' · ')}
            </span>
          </button>
          {mode === 'selected' ? (
            <button type="button" className="ts-icon-clear" onClick={() => removeMatch(match.id)} aria-label={t('tsRemoveMatch')}>
              ×
            </button>
          ) : null}
        </div>
        {wishSummary?.hasPrefs ? (
          <div className="ts-wish-summary">
            {wishSummary.hasAnswers ? (
              <>
                {renderWishLines(homeName, wishSummary.homeYes, wishSummary.homeNo)}
                {renderWishLines(guestName, wishSummary.awayYes, wishSummary.awayNo)}
              </>
            ) : (
              <div className="ts-wish-na">{t('tsWishNoAnswer')}</div>
            )}
          </div>
        ) : null}
      </div>
    );
  };

  const hasMoreMatches = foundMatches.length < matchesTotal;

  return (
    <div className="layoutWrapper st-layout ts-layout">
      <div className="matchCenterWrapper st-page ts-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs items={[{ label: t('navTimeSlots') }]} />
          </div>
        </div>

        {!champId && !champsLoading ? <div className="st-message error">{t('gmNeedChamp')}</div> : null}

        <div className="ts-grid">
          <section className="st-block ts-block ts-block-addmatch">
            <h2 className="st-block-header">{t('tsAddMatch')}</h2>
            <div className="ts-fields ts-fields-cascade">
              <label className="st-field">
                <span>{t('gmCountry')}</span>
                <select className="st-select" value={countryId} onChange={(event) => handleCountryChange(event.target.value)} disabled={!champId}>
                  <option value="">{t('tsSelectCountry')}</option>
                  {countries.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
              </label>
              <label className="st-field">
                <span>{t('tsLeague')}</span>
                <select className="st-select" value={leagueId} onChange={(event) => handleLeagueChange(event.target.value)} disabled={!countryId}>
                  <option value="">{t('tsSelectLeague')}</option>
                  {leagues.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
              </label>
              <label className="st-field">
                <span>{t('tsSeason')}</span>
                <select className="st-select" value={seasonId} onChange={(event) => handleSeasonChange(event.target.value)} disabled={!leagueId}>
                  <option value="">{t('tsSelectSeason')}</option>
                  {seasons.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
              </label>
              <label className="st-field">
                <span>{t('tsTour')}</span>
                <select className="st-select" value={stageId} onChange={(event) => setStageId(event.target.value)} disabled={!seasonId}>
                  <option value="">{t('tsSelectTour')}</option>
                  {stages.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
              </label>
            </div>
            <div className="st-search ts-match-search">
              <FiSearch className="st-search-icon" />
              <input
                type="text"
                className="st-search-input"
                placeholder={t('tsSearchTeams')}
                value={matchSearch}
                disabled={!seasonId}
                onChange={(event) => setMatchSearch(event.target.value)}
              />
              {matchSearch ? (
                <button type="button" className="st-search-clear" onClick={() => setMatchSearch('')}>×</button>
              ) : null}
            </div>
            <div className="ts-scroll">
              {loadingMatches ? (
                <div className="st-loading">
                  <Loader type="TailSpin" color="#3ad1a0" height={36} width={36} />
                  <p>{t('tsMatchesLoading')}</p>
                </div>
              ) : null}
              {!loadingMatches && seasonId && !foundMatches.length ? <div className="ts-empty">{t('tsMatchesEmpty')}</div> : null}
              {!seasonId ? <div className="ts-empty">{t('tsPickCascade')}</div> : null}
              {foundMatches.map((match) => renderMatchRow(match, 'found'))}
              {hasMoreMatches ? (
                <button
                  type="button"
                  className="st-btn st-btn-secondary"
                  disabled={loadingMoreMatches}
                  onClick={() => loadMatches(foundMatches.length, true)}
                >
                  {loadingMoreMatches ? t('tsLoading') : t('tsLoadMore')}
                </button>
              ) : null}
            </div>
          </section>

          <section className="st-block ts-block ts-block-games">
            <h2 className="st-block-header">{t('tsGames')}</h2>
            <div className="ts-scroll">
              {!selectedMatches.length ? <div className="ts-empty">{t('tsGamesEmpty')}</div> : null}
              {selectedMatches.length ? <div className="ts-hint">{t('tsAssignHint')}</div> : null}
              {selectedMatches.map((match) => renderMatchRow(match, 'selected'))}
            </div>
            <div className="ts-block-actions">
              <button
                type="button"
                className="st-btn st-btn-danger"
                disabled={!selectedMatches.length || !canWriteMatches || Boolean(busy)}
                onClick={handleDeleteWishes}
              >
                {t('tsDeleteWishes')}
              </button>
              <button
                type="button"
                className="st-btn st-btn-primary"
                disabled={!canOpenWishes || Boolean(busy)}
                onClick={handleOpenWishes}
              >
                {t('tsOpenWishes')}
              </button>
              <button
                type="button"
                className="st-btn st-btn-primary"
                disabled={!canAutoAssign || Boolean(busy)}
                onClick={handleAutoAssign}
              >
                {t('tsAutoAssign')}
              </button>
            </div>
          </section>

          <section className="st-block ts-block ts-block-addslot">
            <h2 className="st-block-header">{t('tsAddSlot')}</h2>
            <div className="ts-fields ts-fields-slot">
              <div className="st-field">
                <span>{t('tsStadium')}</span>
                <StadiumPicker
                  stadium={stadium}
                  disabled={!champId}
                  onSelect={handleStadiumSelect}
                  onClear={() => {
                    setStadium(null);
                    setPitches([]);
                    setPitchId('');
                  }}
                />
              </div>
              {needPitch ? (
                <label className="st-field">
                  <span>{t('tsPitch')}</span>
                  <select
                    className="st-select"
                    value={pitchId}
                    onChange={(event) => setPitchId(event.target.value)}
                  >
                    <option value="">{t('tsSelectPitch')}</option>
                    {pitches.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                  </select>
                </label>
              ) : null}
              <label className="st-field">
                <span>{t('tsTimezone')}</span>
                <select
                  className="st-select"
                  value={timezoneOverride}
                  onChange={(event) => setTimezoneOverride(event.target.value)}
                  disabled={!champId}
                >
                  <option value="">{t('tsTimezoneAuto', { tz: resolvedTimezone })}</option>
                  {SCHEDULER_TIMEZONE_OPTIONS.map((tz) => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </label>
              {stadium ? (
                <>
                  <label className="st-field ts-dt-field">
                    <span>{t('tsStart')}</span>
                    <div className="ts-dt">
                      <input
                        type="date"
                        className="st-input"
                        value={startDate}
                        onChange={(event) => {
                          const value = event.target.value;
                          setStartDate(value);
                          if (!endDate) setEndDate(value);
                        }}
                      />
                      <input
                        type="time"
                        className="st-input"
                        value={startTime}
                        onChange={(event) => setStartTime(event.target.value)}
                      />
                    </div>
                  </label>
                  <label className="st-field ts-dt-field">
                    <span>{t('tsEnd')}</span>
                    <div className="ts-dt">
                      <input
                        type="date"
                        className="st-input"
                        value={endDate}
                        onChange={(event) => setEndDate(event.target.value)}
                      />
                      <input
                        type="time"
                        className="st-input"
                        value={endTime}
                        onChange={(event) => setEndTime(event.target.value)}
                      />
                    </div>
                  </label>
                  <label className="st-field">
                    <span>{t('tsDuration')}</span>
                    <input
                      type="number"
                      min="1"
                      className="st-input"
                      value={duration}
                      onChange={(event) => setDuration(event.target.value)}
                    />
                  </label>
                  <div className="st-field ts-add-slot-btn">
                    <span>&nbsp;</span>
                    <button type="button" className="st-btn st-btn-primary" disabled={!canGenerate} onClick={handleAddSlots}>
                      {t('tsAddSlots')}
                    </button>
                  </div>
                </>
              ) : (
                <div className="st-field ts-add-slot-btn">
                  <span>&nbsp;</span>
                  <button type="button" className="st-btn st-btn-primary" disabled={!canGenerate} onClick={handleAddSlots}>
                    {t('tsAddSlots')}
                  </button>
                </div>
              )}
            </div>
          </section>

          <section className="st-block ts-block ts-block-slots">
            <h2 className="st-block-header">{t('tsSlots')}</h2>
            <div className="ts-scheme-bar">
              <input
                type="text"
                className="st-input"
                value={schemeName}
                placeholder={t('tsSchemeNamePh')}
                onChange={(event) => setSchemeName(event.target.value)}
              />
              <select className="st-select" value={schemeId} onChange={(event) => setSchemeId(event.target.value)} disabled={loadingSchemes}>
                <option value="">{loadingSchemes ? t('tsLoading') : t('tsSelectScheme')}</option>
                {schemes.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
              <button type="button" className="st-btn st-btn-secondary" disabled={!schemeId || Boolean(busy)} onClick={handleLoadScheme}>
                {t('tsLoadScheme')}
              </button>
              <button
                type="button"
                className="st-btn st-btn-danger"
                disabled={!schemeId || !canWriteSchemes || Boolean(busy)}
                onClick={handleDeleteScheme}
              >
                <FiTrash2 />
                {t('tsDeleteScheme')}
              </button>
            </div>
            <div className="ts-scroll ts-slots-scroll">
              {!slots.length ? <div className="ts-empty">{t('tsSlotsEmpty')}</div> : null}
              {groupedSlots.map((dateGroup) => (
                <div key={dateGroup.dateKey} className="ts-day">
                  <h3>{dateGroup.dateLabel}</h3>
                  {dateGroup.stadiums.map((stadiumGroup) => (
                    <div key={`${dateGroup.dateKey}-${stadiumGroup.stadium_id}`} className="ts-stadium-group">
                      <h4>{stadiumGroup.stadium_name || t('tsStadium')}</h4>
                      {stadiumGroup.pitches.map((pitchGroup) => (
                        <div key={`${stadiumGroup.stadium_id}-${pitchGroup.pitch_id || 'none'}`} className="ts-pitch-group">
                          {pitchGroup.pitch_name ? <div className="ts-pitch-name">{pitchGroup.pitch_name}</div> : null}
                          <div className="ts-slot-grid">
                            {pitchGroup.slots.map((slot) => {
                              const match = slot.pendingMatch || slot.match;
                              const wishStatus = match ? slotMatchWishStatus(match, slot, preferences) : null;
                              const stamp = formatDayMonthTime(
                                slotCalendarDate(slot, weekDate),
                                slot.time || formatClock(slot.hour, slot.minute),
                              );
                              const wishClass = !match
                                ? ''
                                : (wishStatus?.bothOk
                                  ? 'is-wish-ok'
                                  : (wishStatus?.bothBad
                                    ? 'is-wish-bad'
                                    : 'is-wish-mixed'));
                              return (
                                <div
                                  key={slot.clientKey}
                                  className={`ts-slot ${match ? 'has-match' : ''} ${slot.pendingMatch ? 'is-pending' : ''} ${wishClass}`}
                                  onClick={() => {
                                    if (!match && activeMatchId) placeMatchOnSlot(slot.clientKey);
                                  }}
                                >
                                  <div className="ts-slot-time">
                                    <FiClock />
                                    {match ? stamp : (slot.time || formatClock(slot.hour, slot.minute))}
                                  </div>
                                  {match ? (
                                    <div className="ts-slot-match">
                                      <MatchTeams match={match} />
                                      <span className="ts-fits" title={t('tsSlotWishHint')}>
                                        <FitsMark value={wishStatus?.home ?? null} title={t('tsWishHome')} />
                                        <FitsMark value={wishStatus?.away ?? null} title={t('tsWishAway')} />
                                      </span>
                                    </div>
                                  ) : (
                                    <div className="ts-slot-empty">{activeMatchId ? t('tsDropHere') : t('tsEmptySlot')}</div>
                                  )}
                                  <button
                                    type="button"
                                    className="ts-slot-remove"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      if (match) clearSlotMatch(slot.clientKey);
                                      else removeSlot(slot.clientKey);
                                    }}
                                    aria-label={match ? t('tsDetachMatch') : t('tsRemoveSlot')}
                                    title={match ? t('tsDetachMatch') : t('tsRemoveSlot')}
                                  >
                                    <FiX />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <div className="ts-block-actions">
              <button type="button" className="st-btn st-btn-primary" disabled={!canAssign || Boolean(busy)} onClick={handleAssignTime}>
                {t('tsAssignTime')}
              </button>
              <button
                type="button"
                className="st-btn st-btn-secondary"
                disabled={!canWriteSchemes || !slots.length || Boolean(busy)}
                onClick={handleSaveScheme}
              >
                {t('tsSaveScheme')}
              </button>
              <button type="button" className="st-btn st-btn-secondary" disabled={!slots.length} onClick={handleSaveText}>
                <FiDownload />
                {t('tsSaveText')}
              </button>
            </div>
          </section>
        </div>

        <CustomModal
          styles={{ width: 420, height: 'auto', paddingLeft: 32, paddingRight: 32 }}
          visible={Boolean(infoModal.text)}
          closeModal={closeInfo}
        >
          <div className="st-delete-modal">
            <div className="customModalHeader">{infoModal.type === 'error' ? t('tsErrorTitle') : t('tsOkTitle')}</div>
            <p style={{ whiteSpace: 'pre-wrap' }}>{infoModal.text}</p>
            <div className="st-delete-actions">
              <button type="button" className="btn-modal btn-primary-modal" onClick={closeInfo}>
                {t('tsOk')}
              </button>
            </div>
          </div>
        </CustomModal>

        <CustomModal
          styles={{ width: 420, height: 'auto', paddingLeft: 32, paddingRight: 32 }}
          visible={Boolean(confirmModal)}
          closeModal={closeConfirm}
        >
          <div className="st-delete-modal">
            <div className="customModalHeader">{confirmModal?.title}</div>
            <p>{confirmModal?.text}</p>
            <div className="st-delete-actions">
              <button type="button" className="btn-modal btn-secondary-modal" onClick={closeConfirm}>
                {t('cancel')}
              </button>
              <button
                type="button"
                className="btn-modal btn-danger-modal"
                disabled={Boolean(busy)}
                onClick={() => {
                  if (confirmModal?.action === 'scheme') executeDeleteScheme();
                  if (confirmModal?.action === 'wishes') executeDeleteWishes();
                }}
              >
                {t('delete')}
              </button>
            </div>
          </div>
        </CustomModal>
      </div>
    </div>
  );
}
